import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Zap, Power, Mail, MessageSquare, MessageCircle } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { Skeleton, ErrorState } from "@/components/BookingShell";
import { Pill, CHANNEL_LABEL, TRIGGER_LABEL } from "./shared";

const CHANNEL_ICON = { email: Mail, sms: MessageSquare, whatsapp: MessageCircle };
const OFFSET_UNITS = ["minutes", "hours", "days"];

export function CommsRules() {
    const [rules, setRules] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [meta, setMeta] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [showNew, setShowNew] = useState(false);

    const load = () => {
        setError(null);
        Promise.all([
            staffApi.commsListRules(),
            staffApi.commsListTemplates({ status: "published" }),
            staffApi.commsMeta(),
        ]).then(([r, t, m]) => {
            setRules(r.rules || []);
            setTemplates(t.templates || []);
            setMeta(m);
        }).catch(setError);
    };
    useEffect(load, []);

    const publishedByChannel = useMemo(() => {
        const m = { email: [], sms: [], whatsapp: [] };
        (templates || []).forEach((t) => { if (m[t.channel]) m[t.channel].push(t); });
        return m;
    }, [templates]);

    const doDelete = async (id) => {
        if (!window.confirm("Delete this rule?")) return;
        setBusy(true);
        try { await staffApi.commsDeleteRule(id); toast.success("Deleted"); load(); }
        catch (e) { toast.error(e.message || "Delete failed"); }
        finally { setBusy(false); }
    };

    const doToggle = async (rule) => {
        setBusy(true);
        try { await staffApi.commsPatchRule(rule.id, { active: !rule.active }); load(); }
        catch (e) { toast.error(e.message || "Toggle failed"); }
        finally { setBusy(false); }
    };

    if (error) return <ErrorState error={error} onRetry={load} />;

    return (
        <div className="space-y-4" data-testid="comms-rules-page">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h2 className="font-serif text-2xl text-white">Trigger rules</h2>
                    <p className="text-sm text-white/50 mt-1">Wire lifecycle events to published templates. Time-based triggers accept a configurable offset.</p>
                </div>
                <button onClick={() => setShowNew(true)} data-testid="new-rule-btn"
                        className="h-9 px-4 rounded-full bg-gold text-navy font-semibold text-xs inline-flex items-center gap-1">
                    <Plus className="h-3.5 w-3.5" /> New rule
                </button>
            </div>

            {!rules ? <Skeleton className="h-32" /> : rules.length === 0 ? (
                <div className="rounded-2xl border hairline p-8 text-center" data-testid="rules-empty">
                    <Zap className="h-6 w-6 mx-auto text-white/30 mb-2" strokeWidth={1.5} />
                    <p className="text-white/60 text-sm">No rules yet. Add a rule to define when and how a template should be scheduled.</p>
                </div>
            ) : (
                <ul className="space-y-2" data-testid="rules-list">
                    {rules.map((r) => (
                        <li key={r.id} className="rounded-2xl border hairline p-4" data-testid={`rule-row-${r.id}`}>
                            <div className="flex flex-wrap items-start gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-white font-ui text-sm">{r.name}</span>
                                        {r.active
                                            ? <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-300">Active</span>
                                            : <span className="text-[10px] uppercase tracking-[0.18em] text-white/40">Paused</span>}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.16em] text-white/45 flex flex-wrap gap-2 mt-1">
                                        <span>Trigger: {TRIGGER_LABEL[r.trigger] || r.trigger}</span>
                                        {r.offset && <span>· Offset: {r.offset.value} {r.offset.unit} {r.offset.direction === "before_appointment" ? "before" : "after"}</span>}
                                    </div>
                                    <div className="mt-2 text-xs text-white/70">
                                        Template: <span className="text-white">{r.template?.name || "—"}</span>{" "}
                                        <span className="text-white/40">({r.template?.channel})</span>
                                    </div>
                                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                                        {(r.channels || []).map((c) => {
                                            const Icon = CHANNEL_ICON[c] || Mail;
                                            return (
                                                <span key={c} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-white/70 border hairline rounded-full px-2 py-0.5">
                                                    <Icon className="h-3 w-3" strokeWidth={1.5} /> {CHANNEL_LABEL[c]}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                                <div className="flex gap-2 items-center">
                                    <button onClick={() => doToggle(r)} disabled={busy} data-testid={`toggle-rule-${r.id}`}
                                            className="h-8 px-3 rounded-full border hairline text-[11px] text-white/70 hover:text-gold inline-flex items-center gap-1">
                                        <Power className="h-3 w-3" /> {r.active ? "Pause" : "Activate"}
                                    </button>
                                    <button onClick={() => doDelete(r.id)} disabled={busy} data-testid={`delete-rule-${r.id}`}
                                            className="h-8 px-3 rounded-full border hairline text-[11px] text-red-300 hover:border-red-300/50 inline-flex items-center gap-1">
                                        <Trash2 className="h-3 w-3" /> Delete
                                    </button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {showNew && meta && (
                <NewRuleModal
                    meta={meta}
                    templates={templates}
                    onClose={() => setShowNew(false)}
                    onCreated={() => { setShowNew(false); load(); }}
                    publishedByChannel={publishedByChannel}
                />
            )}
        </div>
    );
}

function NewRuleModal({ meta, templates, onClose, onCreated, publishedByChannel }) {
    const [form, setForm] = useState({
        name: "",
        trigger: meta.triggers[0]?.id || "manual",
        template_id: "",
        channels: [],
        offset: { value: 24, unit: "hours" },
        active: true,
        description: "",
    });
    const [busy, setBusy] = useState(false);
    const triggerMeta = useMemo(() => meta.triggers.find((t) => t.id === form.trigger), [meta, form.trigger]);

    // When template changes, auto-add its channel
    useEffect(() => {
        const t = templates.find((tt) => tt.id === form.template_id);
        if (t && !form.channels.includes(t.channel)) {
            setForm((f) => ({ ...f, channels: [...new Set([...f.channels, t.channel])] }));
        }
        // eslint-disable-next-line
    }, [form.template_id]);

    const toggleChannel = (c) => {
        setForm((f) => ({
            ...f,
            channels: f.channels.includes(c) ? f.channels.filter((x) => x !== c) : [...f.channels, c],
        }));
    };

    const save = async () => {
        if (!form.name.trim()) { toast.error("Rule name is required"); return; }
        if (!form.template_id) { toast.error("Select a template"); return; }
        if (form.channels.length === 0) { toast.error("At least one channel is required"); return; }
        setBusy(true);
        try {
            const payload = {
                name: form.name.trim(),
                trigger: form.trigger,
                template_id: form.template_id,
                channels: form.channels,
                active: form.active,
                description: form.description,
                offset: triggerMeta?.offset_kind ? { value: Number(form.offset.value) || 0, unit: form.offset.unit } : undefined,
            };
            await staffApi.commsCreateRule(payload);
            toast.success("Rule created");
            onCreated();
        } catch (e) { toast.error(e.message || "Create failed"); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4 overflow-y-auto py-6" data-testid="new-rule-modal" onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-3xl glass-strong p-6 space-y-3 max-h-[90vh] overflow-y-auto">
                <h3 className="font-serif text-2xl text-white">New rule</h3>

                <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">Name</div>
                    <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rule-name"
                           placeholder="e.g. 24h reminder — Email + SMS"
                           className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none focus:border-gold" />
                </label>

                <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">Trigger</div>
                    <select value={form.trigger} onChange={(e) => setForm({ ...form, trigger: e.target.value })} data-testid="rule-trigger"
                            className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none">
                        {meta.triggers.map((t) => (
                            <option key={t.id} value={t.id} className="bg-navy">
                                {(TRIGGER_LABEL[t.id] || t.id) + (t.transactional ? "" : " · marketing")}
                            </option>
                        ))}
                    </select>
                </label>

                {triggerMeta?.offset_kind && (
                    <div className="rounded-xl border hairline p-3 bg-white/[0.03]" data-testid="rule-offset-panel">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-2">Offset</div>
                        <div className="flex items-center gap-2">
                            <input type="number" min="0" value={form.offset.value}
                                   onChange={(e) => setForm({ ...form, offset: { ...form.offset, value: e.target.value } })}
                                   data-testid="rule-offset-value"
                                   className="w-24 bg-transparent border hairline rounded-lg px-2 py-1.5 text-white text-sm outline-none focus:border-gold" />
                            <select value={form.offset.unit} onChange={(e) => setForm({ ...form, offset: { ...form.offset, unit: e.target.value } })}
                                    data-testid="rule-offset-unit"
                                    className="bg-transparent border hairline rounded-lg px-2 py-1.5 text-white text-sm outline-none">
                                {OFFSET_UNITS.map((u) => <option key={u} value={u} className="bg-navy">{u}</option>)}
                            </select>
                            <span className="text-xs text-white/60">
                                {triggerMeta.offset_kind === "before_appointment" ? "before appointment" : "after appointment"}
                            </span>
                        </div>
                    </div>
                )}

                <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">Template</div>
                    <select value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value })} data-testid="rule-template"
                            className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none">
                        <option value="" className="bg-navy">— Select a published template —</option>
                        {templates.map((t) => (
                            <option key={t.id} value={t.id} className="bg-navy">{t.name} ({CHANNEL_LABEL[t.channel]})</option>
                        ))}
                    </select>
                    {templates.length === 0 && (
                        <div className="text-[11px] text-amber-300 mt-1.5">Publish at least one template to attach a rule.</div>
                    )}
                </label>

                <div>
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">Delivery channels</div>
                    <div className="flex gap-2 flex-wrap">
                        {meta.channels.map((c) => {
                            const on = form.channels.includes(c);
                            return (
                                <button key={c} onClick={() => toggleChannel(c)} data-testid={`rule-ch-${c}`}
                                        className={`h-8 px-3 rounded-full border text-xs inline-flex items-center gap-1 transition-colors ${
                                            on ? "bg-gold/15 border-gold/40 text-gold" : "border-white/15 text-white/60 hover:border-white/30"
                                        }`}>
                                    {CHANNEL_LABEL[c]}
                                </button>
                            );
                        })}
                    </div>
                    <p className="text-[10px] text-white/40 mt-1">The template's own channel is always included.</p>
                </div>

                <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-white/45 mb-1.5">Description</div>
                    <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="rule-desc"
                           placeholder="Internal note"
                           className="w-full bg-transparent border-b hairline py-2 text-white text-sm outline-none focus:border-gold" />
                </label>

                <label className="flex items-center gap-2 text-sm text-white/70">
                    <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="rule-active"
                           className="accent-gold" />
                    Activate immediately
                </label>

                <div className="flex gap-2 justify-end pt-3">
                    <button onClick={onClose} className="h-10 rounded-full border hairline text-white/70 px-4 text-sm">Cancel</button>
                    <button onClick={save} disabled={busy} data-testid="rule-save"
                            className="h-10 rounded-full bg-gold text-navy px-5 text-sm font-semibold disabled:opacity-40">
                        Create rule
                    </button>
                </div>
            </div>
        </div>
    );
}
